/**
 * familyRelation.js — PROD-426 (patient push 3a). Which relative an extracted family-history
 * entry names, as one of the profile's keys: mother / father / sibling / grandparent, or null.
 *
 * ★★★ THE DEFECT (found by the patient push 1 repair). useOnboardingStore.applyAutofillResult
 *   matched the extractor's free-text relation by SUBSTRING:
 *       /mother|mom/ -> mother, /father|dad/ -> father, /brother|sister|sibling/ -> sibling,
 *       /grand/ -> grandparent
 *   in that order, so "grandmother" matched /mother/ first and a grandmother's condition was
 *   recorded under the MOTHER (and "grandfather" under the father). A clinical-history error.
 *
 * ★★ THE RULE. Whole words only, and the grand- / great- forms are checked FIRST. A relative
 *   by marriage or by a step-relation (mother-in-law, stepfather) is not one of the patient's
 *   blood relatives and is not filed under one (null; the entry is not ticked for anyone).
 *   This is matching the patient's own word, not a clinical decision.
 * ★★ REVIEW r1 MINOR 4 (the PROD-426 class): "mother's father" / "Mother's mother" were filed as
 *   the MOTHER, and a foster or adoptive mother as the mother. Now:
 *   - a parent's parent ("mother's father", "dad's mom", curly or no apostrophe) is a grandparent;
 *   - any other relative OF a parent ("mother's sister", "father's cousin") is none of the four;
 *     "<parent>'s side" names a branch, not the relative, and is read past;
 *   - foster / adoptive / adopted relatives are not blood relatives (null). "birth mother" and
 *     "biological father" stay the parent.
 *
 * Pure. scripts/check-autofill-merge.mjs FR-* executes it through the real store.
 */
// PROD-1025: the one-word grandparent names. "grandma", "grandpa" and "granddad" are also read by the long
//   grand-mother form below; the rest need this list. A bare
//   "pop" or "grand" is NOT here: "pop" is no relative on its own, only the doubled "pop pop" is. Each word
//   is matched whole (\b), so "naan", "nanny", "roma" and "opaque" never read as a grandparent.
const GP_WORDS = 'grandma|grandpa|granny|grannie|gran|grandad|grandaddy|gramps|nana|nanna|nan|nonna|nonno|oma|opa|abuela|abuelo|pop[\\s-]*pop'
export function familyRelation(raw) {
  let s = String(raw || '').toLowerCase().replace(/[\u2018\u2019`]/g, "'").replace(/_/g, ' ').trim()
  if (!s) return null
  if (/\b(foster|adoptive|adopted)\b/.test(s)) return null
  const PARENT = '(?:mother|mom|mum|mama|mommy|father|dad|daddy|papa|parent)'
  // r3 N1: a plural possessive ("parents' siblings") counts like "parent's"
  const POSS = `${PARENT}(?:'?s|s')`
  if (new RegExp(`\\b${POSS}\\s+${PARENT}s?\\b`).test(s)) return 'grandparent'
  // r2 N3: "mother and father's side" names both branches, not the mother
  s = s.replace(new RegExp(`\\b(?:${PARENT}\\s*(?:and|&|\\+|/)\\s*)?${POSS}\\s+side\\b`, 'g'), ' ')
  if (new RegExp(`\\b${POSS}\\s+[a-z]`).test(s)) return null
  // PROD-473 NIT: a sibling's relative ("brother's son", "sisters kids", "sister's husband") is a
  //   niece, nephew or in-law, none of the four. Only a relative noun after it counts (review
  //   r_473 S1): "brothers deceased", "sisters x2", "siblings all" are still siblings. r2 N2: a
  //   plural possessive ("sisters' kids") and a count before the noun ("sister's 2 kids") count too.
  //   Known limit: a noun outside the list stays a sibling ("brother's ex-wife", "brother's twins").
  if (/\b(?:half[\s-]*)?(?:brother|sister|sibling)(?:'?s|s')\s+(?:(?:\d+|two|three|four|five)\s+)?(?:son|daughter|child|children|kid|(?:boy|girl)(?:friend)?|baby|babies|wife|wives|husband|spouse|partner|fianc\w*|grand\w*)(?:s|'s|s')?(?![a-z])/.test(s)) return null
  // PROD-956: a grandparent's sibling or child ("grandma's sister", "grandpa's brother", "granny's kids")
  //   is a great-aunt, great-uncle or an aunt, uncle or parent: none of the four, as "mother's sister" is.
  //   A grandparent's spouse or parent stays a grandparent ("grandma's husband", "grandma's mom").
  if (new RegExp(`\\b(?:(?:great[\\s-]*)*grand[\\s-]*(?:mother|father|parent|mom|mum|dad|ma|pa|mama|papa)|${GP_WORDS})(?:'?s|s')\\s+(?:half[\\s-]*)?(?:brother|sister|sibling|twin|aunt|uncle|cousin|niece|nephew|son|daughter|child|children|kid)s?(?![a-z])`).test(s)) return null
  if (/\bin[\s-]*laws?\b/.test(s) || /\bstep[\s-]*(mother|father|mom|dad|parent|brother|sister|sibling|son|daughter)s?\b/.test(s)) return null
  if (/\b(great[\s-]*)*grand[\s-]*(mother|father|parent|mom|mum|dad|ma|pa|mama|papa)s?\b/.test(s) || new RegExp(`\\b(?:${GP_WORDS})\\b`).test(s)) return 'grandparent'
  if (/\b(mother|mom|mum|mama|mommy)\b/.test(s)) return 'mother'
  if (/\b(father|dad|daddy|papa)\b/.test(s)) return 'father'
  // PROD-956: a plural parent noun ("my dads", "both moms") names that parent, as the singular does
  if (/\b(mothers|moms|mums|mamas|mommies)\b/.test(s)) return 'mother'
  if (/\b(fathers|dads|daddies|papas)\b/.test(s)) return 'father'
  if (/\b(half[\s-]*)?(brother|sister|sibling)s?\b/.test(s) || /\btwins?\b/.test(s)) return 'sibling'
  return null
}

/**
 * PROD-473 NIT: every relative an entry names. "mother and father" was filed under the mother
 * alone (familyRelation reads one relative). A relation joined by "and", "&", "+", "/", "," or
 * ";" names each part; "parents" / "both parents" is the mother and the father. A part that names
 * no blood relative ("aunt", "mother-in-law") adds none, and the others still count. From the
 * first part with a possessive on, the rest is read whole, as before, because a possessive can
 * cover the parts after it ("mother's father and mother" is a grandparent); the parts before it
 * stand alone ("father and brother's son" is the father). "or" does not split: "mother or father"
 * is read whole by familyRelation, which ticks the first relative it checks (here the mother).
 * \u2605 review r2 S1: a JOINT possessive covers both nouns of a pair. When the part just before the
 *   possessive is joined to it by "and", "&", "+" or "/" and the two nouns are a pair (mother and
 *   father, brother and sister), the whole phrase is read as one possessive: "mom and dad's
 *   siblings", "mother and father's brothers" and "brother and sister's kids" are none of the four
 *   (they were the mother / the sibling). The same word twice is no pair ("Mother and mother's
 *   sister" is the mother), nor is a parent with a sibling ("father and brother's son" is the
 *   father). Known limits, ambiguous in the words themselves: a parent pair owning ONE parent
 *   ("mom & dad's mom") keeps the split reading (the mother and a grandparent, as r2 measured it);
 *   "mom, dad's siblings" (a comma) is not joint and ticks the mother.
 * \u2605 review r3: a parent noun ending in "s" right before another word is a possessive without its
 *   apostrophe ("mom and dads siblings" reads as "mom and dad's siblings", none), as familyRelation
 *   already reads "dads siblings". A part after the first that begins with "his", "her" or "their"
 *   names a relative of the person before it, not of the patient: "my mom and her sister" is the
 *   mother alone, "my mom and her parents" never the father. A lone "her sister" / "his parents"
 *   is read as before. A possessive part with its own determiner ("my brother and my sister's
 *   son") or owning one spouse ("sister & brother's wife") is not joint: the first part stands.
 * Returns the profile keys in the order named, without repeats; [] for none.
 */
export function familyRelations(raw) {
  const s = String(raw || '').toLowerCase().replace(/[\u2018\u2019`]/g, "'").replace(/_/g, ' ').trim()
    .replace(/\b(mother|mom|mum|mama|mommy|father|dad|daddy|papa)s\s+(?!(?:and|or)\b)(?=[a-z])/g, "$1's ")
  if (!s) return []
  const PARENTS = /^(?:both\s+)?(?:(?:my|his|her|the)\s+)?parents$/
  const one = (part) => (PARENTS.test(part) ? ['mother', 'father'] : [familyRelation(part)].filter(Boolean))
  // r3 S2: a later part on "his" / "her" / "their" is the previous relative's relative
  //   PROD-956: unless the first part is itself on "his" / "her" / "their" and names a blood relative
  //   (a caregiver's voice: "her mother and her sister" is the mother and the sibling)
  const HER = /^(?:his|her|their)\b/
  const caregiver = () => HER.test(parts[0]) && one(parts[0]).length > 0
  const own = (part, i) => (i > 0 && HER.test(part) && !caregiver() ? [] : one(part))
  // the parts, each with the separator that joins it to the part before it
  const bits = s.split(/\s*(,|;|&|\+|\/|\band\b)\s*/)
  const parts = []
  const joins = []
  bits.forEach((b, i) => { if (i % 2 === 0 && b.trim()) { parts.push(b.trim()); joins.push(i ? bits[i - 1] : '') } })
  let k = parts.findIndex((p) => /'s\b|s'(?=\s|$)/.test(p))
  if (k > 0 && joins[k] !== ',' && joins[k] !== ';' && jointPair(parts[k - 1], parts[k])) k -= 1
  const found = k < 0 ? parts.flatMap(own) : [...parts.slice(0, k).flatMap(own), ...own(parts.slice(k).join(' and '), k)]
  return [...new Set(found)]
}

// r2 S1: `before` ("mom") and the possessive part `poss` ("dad's siblings") name the two halves of
//   a pair: a mother and a father, or a brother and a sister. Not when a parent pair owns a single
//   parent ("mom & dad's mom"; see the known limits above).
function jointPair(before, poss) {
  const KINDS = [['m', /^(?:mother|mom|mum|mama|mommy)s?$/], ['f', /^(?:father|dad|daddy|papa)s?$/],
    ['b', /^(?:half[\s-]*)?brothers?$/], ['s', /^(?:half[\s-]*)?sisters?$/]]
  const kindOf = (w) => (KINDS.find(([, re]) => re.test(w)) || [])[0]
  // r3 N2: "my sister's son" is the patient's own sister's son; a pair owns no single spouse
  if (/^(?:my|his|her|their|the)\s/.test(poss)) return false
  const m = poss.match(/^(?:(?:my|his|her|the)\s+)?([a-z][a-z\s-]*?)(?:'s|s')(?:\s+(.*))?$/)
  // PROD-956: one or several spouses ("sister's husbands") are the sibling's own, not a pair's
  if (!m || /^(?:wife|wives|husbands?|spouses?|partners?)(?![a-z])/.test(m[2] || '')) return false
  // the last word before ("my mom", "aunt on mom" -> "mom")
  const pair = [kindOf((before.match(/(?:half[\s-]*)?[a-z]+$/) || [''])[0]), kindOf(m[1])].sort().join('')
  if (pair === 'fm') return !/^(?:mother|mom|mum|mama|mommy|father|dad|daddy|papa|parent)\b/.test(m[2] || '')
  return pair === 'bs'
}
